/**
 * Single source of truth for public product messaging.
 *
 * Home, Platform, Field Intelligence, Traceability and the Footer all read
 * from this file so the positioning ("the intelligent operating system for
 * agriculture") is stated the same way everywhere.
 */

export const positioning = {
  eyebrow: 'AI-ENABLED AGRICULTURAL OPERATIONS',
  headline: 'Manage agriculture from field to market.',
  supporting:
    'eSanza connects farm management, AI-assisted field intelligence, traceability, supply-chain operations and B2B market access in one agricultural operating system.',
  definition:
    'eSanza is an AI-enabled agricultural operating system connecting farm management, field intelligence, traceability, supply-chain operations and B2B market access in one platform.',
  short: 'The intelligent operating system for agriculture.',
  origin: 'Built in Rwanda. Designed for agriculture everywhere.',
  originFooter: 'Built in Rwanda. Designed for global agriculture.',
  marketplace:
    'Verified agricultural supply, connected directly to operations and traceability.',
  ai: 'Agricultural intelligence grounded in field context.',
  traceability: 'From field activity to final buyer.',
} as const;

export const ctas = {
  primary: { label: 'Explore the Platform', to: '/platform' },
  secondary: { label: 'Start Your Workspace', to: '/try-esanza' },
  tertiary: { label: 'Explore eSanza Market', to: '/marketplace' },
} as const;

/** The conceptual flow every page uses to explain how the system connects. */
export const operatingFlow = [
  'Farm',
  'Field Data',
  'AI Intelligence',
  'Field Action',
  'Traceability',
  'Harvest',
  'Warehouse',
  'Marketplace',
  'Logistics',
  'Buyer',
  'Analytics',
] as const;

/** The high-level ecosystem chain shown on the homepage. */
export const ecosystem = [
  'Farm Management',
  'AI Field Intelligence',
  'Traceability',
  'Supply Chain',
  'Marketplace',
  'Logistics',
  'Analytics',
] as const;

export interface Pillar {
  id: string;
  title: string;
  body: string;
  points: string[];
}

export const pillars: Pillar[] = [
  {
    id: 'farm-operations',
    title: 'Farm Operations',
    body: 'The record of who grows what, where, and what has happened in the field.',
    points: [
      'Farmers',
      'Farms & fields',
      'Crops & trees',
      'Activities & assignments',
      'Scouting & inspections',
      'Issues & field records',
    ],
  },
  {
    id: 'field-intelligence',
    title: 'AI Field Intelligence',
    body: 'Context-aware agronomic guidance produced inside everyday field work, not beside it.',
    points: [
      'Agricultural advisory',
      'Weather intelligence',
      'Field risk detection',
      'Explainable recommendations',
      'Human review for high-risk actions',
    ],
  },
  {
    id: 'traceability',
    title: 'Traceability',
    body: 'An unbroken chain from the field to the buyer, built from work that was already recorded.',
    points: [
      'Farm → Field',
      'Crop / Tree → Activity',
      'Harvest → Lot',
      'Warehouse → Shipment',
      'Buyer',
    ],
  },
  {
    id: 'supply-chain',
    title: 'Supply Chain Operations',
    body: 'How production becomes commercial supply without re-entering the same data twice.',
    points: [
      'Harvest aggregation',
      'Warehouse & stock',
      'Quality & grading',
      'Packing & fulfilment',
      'Inventory movement',
    ],
  },
  {
    id: 'marketplace',
    title: 'B2B Marketplace',
    body: 'Verified supply meets demand, with the records already attached.',
    points: [
      'Verified suppliers',
      'Corporate buyers',
      'RFQs & quotations',
      'Contracts & orders',
      'Procurement workflow',
    ],
  },
  {
    id: 'logistics',
    title: 'Logistics & Delivery',
    body: 'Moving confirmed orders from collection point to buyer with evidence.',
    points: [
      'Pickup & transport assignment',
      'Shipment tracking',
      'Delivery evidence',
      'Proof of delivery',
    ],
  },
];

export interface Audience {
  title: string;
  body: string;
}

export const audiences: Audience[] = [
  { title: 'Farmers', body: 'Daily tasks, field records, advice and market access from a phone.' },
  { title: 'Agronomists / Extension teams', body: 'Field risk, review queues and supervised recommendations.' },
  { title: 'Farm managers', body: 'People, work, evidence and schedules across every field.' },
  { title: 'Cooperatives', body: 'Member production, aggregation and collective market readiness.' },
  { title: 'Agribusinesses', body: 'Multi-farm supply, quality, inventory and fulfilment control.' },
  { title: 'Buyers / Procurement teams', body: 'Verified supply, RFQs, quotations, contracts and delivery.' },
  { title: 'Development organisations', body: 'Programme monitoring grounded in real field activity.' },
  { title: 'Public institutions', body: 'Aggregate production conditions and verified reporting.' },
];

/** The full traceability chain, in the order the system records it. */
export const traceChain = [
  'Farm',
  'Field',
  'Tree / Crop',
  'Activity',
  'Harvest',
  'Lot',
  'Warehouse',
  'Shipment',
  'Buyer',
] as const;

/** How a listing is produced from operational data rather than typed twice. */
export const marketSupplyFlow = [
  'Production',
  'Farm Verification',
  'Traceability',
  'Harvest Lot',
  'Quality',
  'Available Supply',
  'Marketplace Listing',
  'RFQ / Order / Contract',
  'Aggregation',
  'Logistics',
  'Delivery',
  'Settlement',
] as const;

export interface PlatformModule {
  id: string;
  number: string;
  title: string;
  summary: string;
  does: string;
  users: string;
  connects: string;
  status?: 'Live' | 'Pilot' | 'Beta' | 'Planned';
}

/** The ten modules of the operating system, in operating order. */
export const platformModules: PlatformModule[] = [
  {
    id: 'farm-management',
    number: '01',
    title: 'Farm Management',
    summary: 'The master record of the agricultural operation.',
    does: 'Register farmers, farms, fields, crops and trees, and keep the geometry, ownership and season records that everything else attaches to.',
    users: 'Farm managers, cooperative staff, project teams',
    connects: 'Every field activity, traceability record and listing references these records.',
    status: 'Live',
  },
  {
    id: 'field-operations',
    number: '02',
    title: 'Field Operations',
    summary: 'Work planned, assigned, executed and evidenced.',
    does: 'Plan activities, assign them to people, record a field diary, run scouting and inspections, and raise and close issues.',
    users: 'Farm managers, agronomists, field facilitators, farmers',
    connects: 'Planned work becomes traceability evidence; issues feed the intelligence layer.',
    status: 'Live',
  },
  {
    id: 'field-intelligence',
    number: '03',
    title: 'AI Field Intelligence',
    summary: 'Agricultural intelligence grounded in field context.',
    does: 'Combine field history, weather and crop context to propose guidance, flag risk, and explain why — with agronomist review before high-risk actions.',
    users: 'Farmers, agronomists, extension teams, institutions',
    connects: 'Reads field records, writes recommended actions back as assignable work.',
    status: 'Beta',
  },
  {
    id: 'traceability',
    number: '04',
    title: 'Traceability',
    summary: 'From field activity to final buyer.',
    does: 'Record the chain farm → field → tree or crop → activity → harvest → lot → warehouse → shipment → buyer, and publish verifiable trace certificates.',
    users: 'Farmers, agronomists, buyers, auditors',
    connects: 'Turns operational records into proof that the marketplace and logistics rely on.',
    status: 'Live',
  },
  {
    id: 'training',
    number: '05',
    title: 'Training',
    summary: 'Capability delivered where the work happens.',
    does: 'Schedule sessions, track attendance, distribute learning material, run quizzes and recognise progression.',
    users: 'Extension teams, cooperatives, development organisations',
    connects: 'Training records sit beside the field records they improve.',
    status: 'Live',
  },
  {
    id: 'inputs',
    number: '06',
    title: 'Inputs & Farm Resources',
    summary: 'What was supplied, to whom, and what is owed.',
    does: 'Record input distribution, invoices, balances and resource qualification against farms and farmers.',
    users: 'Cooperatives, project teams, finance officers',
    connects: 'Links cost of production to the records that later become supply.',
    status: 'Live',
  },
  {
    id: 'warehouse',
    number: '07',
    title: 'Warehouse & Aggregation',
    summary: 'Where harvest becomes stock.',
    does: 'Receive incoming lots, manage stock and internal movement, record quality, and track storage conditions and fulfilment.',
    users: 'Warehouse teams, aggregators, logistics coordinators',
    connects: 'Receives harvest lots from traceability and releases stock to the marketplace.',
    status: 'Live',
  },
  {
    id: 'marketplace',
    number: '08',
    title: 'Marketplace',
    summary: 'Verified agricultural supply, connected to operations.',
    does: 'Publish listings from real stock, run RFQs, quotations, contracts and orders between verified suppliers and buyers.',
    users: 'Suppliers, buyers, procurement teams',
    connects: 'Draws quantity, quality and traceability from the modules above instead of asking for them again.',
    status: 'Live',
  },
  {
    id: 'logistics',
    number: '09',
    title: 'Logistics',
    summary: 'Confirmed orders moved and proven.',
    does: 'Arrange pickup or delivery, assign transport, record shipments, capture delivery evidence and proof of delivery.',
    users: 'Suppliers, logistics coordinators, buyers',
    connects: 'Closes the order loop and writes delivery evidence back to the record.',
    status: 'Pilot',
  },
  {
    id: 'analytics',
    number: '10',
    title: 'Analytics & Reporting',
    summary: 'Operational truth, at every level.',
    does: 'KPIs, maps, field-risk views, production conditions and verified reporting for managers and institutions.',
    users: 'Farm managers, project managers, institutions, platform owners',
    connects: 'Reads every module above; nothing is entered only for reporting.',
    status: 'Live',
  },
];

export default positioning;
